<template>
  <div v-click-outside="closeMap" class="addressSearch">
    <div class="addressSearch__wrapper">
      <v-scroll-y-transition>
        <span v-show="mapsShow">
          <v-btn @click="mapsShow = false" class="addressSearch__mapClose black" color="white" small icon>
            <v-icon small>mdi-close</v-icon>
          </v-btn>
          <div class="addressSearch__map rounded" ref="map"></div>
        </span>
      </v-scroll-y-transition>

      <v-text-field
        @click:clear="resetAddress"
        @change="handleSearchAddress"
        ref="googleAddressAutocomplete"
        :value="googleAddress && googleAddress.address ? googleAddress.address : ''"
        :label="labelLanguage"
        :loading="loading"
        :dense="dense"
        :rules="rules"
        :hint="hintLanguage"
        persistent-hint
        clearable
        outlined
      />

      <v-btn
        v-if="mapsLink"
        @click="mapsShow = true"
        :class="`${small ? 'small' : ''} addressSearch__show-map`"
        color="primary"
        target="_blank"
        small
        link
        text
      >
        <v-icon small>mdi-map-marker-circle</v-icon>
        <span class="caption">
          {{ $t('realty.hint.seeOnMaps') }}
        </span>
      </v-btn>
    </div>
  </div>
</template>

<script>
import { mapLoader } from '@/util/mapLoader'
import { mapActions } from 'vuex'

export default {
  name: 'AddressSearch',

  props: {
    rules: {
      type: Array,
    },

    address: {
      type: String,
      default: null,
    },

    getFocus: {
      type: Boolean,
      default: true,
    },

    label: {
      type: String,
    },

    hint: {
      type: String,
      default: null,
    },

    dense: {
      type: Boolean,
      default: false,
    },

    small: {
      type: Boolean,
      default: false,
    },

    id: {
      type: String,
      required: false,
      default: 'citySelect',
    },

    loading: {
      type: Boolean,
      default: false,
    },

    validate: {
      type: Boolean,
      default: true,
    },

    selectedCountry: {
      type: String,
      default: localStorage.getItem('user_country') ?? 'BR',
    },
  },

  data() {
    return {
      city: null,
      route: null,
      state: null,
      country: null,
      postalCode: null,
      streetNumber: null,
      formattedAddress: null,

      mapsLink: false,
      mapsShow: false,
      mapsField: null,
      mapsGoogle: null,
      mapsAutocomplete: null,
      googleAddress: {},
    }
  },

  computed: {
    mapsOptions() {
      return {
        strictBounds: false,
        componentRestrictions: { country: this.switchCountryCode() },
        fields: ['formatted_address', 'address_components', 'geometry', 'vicinity', 'url'],
      }
    },

    fieldTitle() {
      return this.label
        ? this.label
        : this.$t('onboarding.address.form.address')
    },

    labelLanguage() {
      if (this.selectedCountry === 'BR') return 'Endereço'
      return 'Address'
    },

    hintLanguage() {
      if (this.selectedCountry === 'BR') return 'Ex: Rua Abdon Batista, 80 - Centro, Joinville - SC, 89201-010, Brasil'
      return '123 Eagle St, Brisbane City QLD 4000, Australia'
    },
  },

  watch: {
    selectedCountry() {
      this.resetAddress()
      this.initGooglePlaces()
    },
  },

  methods: {
    ...mapActions(['showErrorMessage']),

    initGooglePlaces() {
      if (!process.env.VUE_APP_GOOGLE_MAPS_API_KEY) throw new Error('Please provide google maps api key')

      mapLoader
        .load()
        .then((google) => {
          this.mapsGoogle = google
          this.mapsAutocomplete = new google.maps.places.Autocomplete(this.mapsField, this.mapsOptions)
        })
    },

    getCompleteAddress(addresses) {
      let streetNumber = null
      let postalCode = null
      let vicinity = null
      let country = null
      let route = null
      let state = null
      let city = null

      const countryComponent = addresses.find((component) => component.types[0] === 'country')

      country = countryComponent.long_name

      for (const component of addresses) {
        const componentType = component.types[0]

        switch (componentType) {
          case 'sublocality_level_1': {
            vicinity = component.short_name
            break
          }

          case 'street_number': {
            streetNumber = component.long_name
            break
          }

          case 'route': {
            route = component.short_name
            break
          }

          case 'postal_code': {
            postalCode = component.long_name
            break
          }

          case 'administrative_area_level_1': {
            state = component.short_name
            break
          }

          case this.loadCityComponentType(country): {
            city = component.long_name
            break
          }
        }
      }

      const fullAddress = {
        streetNumber,
        postalCode,
        vicinity,
        country,
        route,
        state,
        city,
      }

      this.getGoogleAddressData(fullAddress)
    },

    loadCityComponentType(country) {
      return country === 'Australia' ? 'locality' : 'administrative_area_level_2'
    },

    getGoogleAddressData(address) {
      // complete address example
      // R. Abdon Batista, 80 - Centro, Joinville - SC, 89201-010, Brasil
      this.route = address?.route // R. Abdon Batista
      this.streetNumber = address?.streetNumber // 80
      this.vicinity = address?.vicinity // Centro
      this.state = address?.state // SC
      this.city = address?.city // Joinville
      this.postalCode = address?.postalCode // 89201-010
      this.country = address?.country // Brasil

      this.$nextTick(() => {
        this.handleMountAddress()
      })
    },

    handleMountAddress() {
      this.googleAddress = {
        city: this.city,
        route: this.route,
        vicinity: this.vicinity,
        streetNumber: this.streetNumber,
        postalCode: this.postalCode,
        address: this.formattedAddress,
      }

      this.$emit('address', this.googleAddress)
    },

    handleSearchAddress() {
      this.$nextTick(() => {
        this.mapsGoogle.maps.event.addListener(this.mapsAutocomplete, 'place_changed', () => {
          const formatted_address = this.mapsAutocomplete?.getPlace()?.formatted_address
          const address_components = this.mapsAutocomplete?.getPlace()?.address_components

          this.formattedAddress = formatted_address
          this.getCompleteAddress(address_components)

          // beta maps ------------------------------------------
          this.setMarker()
        })
      })
    },

    setMarker() {
      const location = this.mapsAutocomplete?.getPlace()?.geometry?.location
      const lat = location.lat()
      const lng = location.lng()
      const mapContainer = this.$refs.map
      const google = this.mapsGoogle
      const position = { lat, lng }
      const options = { zoom: 18, center: position, disableDefaultUI: false, styles: [{"featureType":"all","elementType":"labels.text","stylers":[{"color":"#878787"}]},{"featureType":"all","elementType":"labels.text.stroke","stylers":[{"visibility":"off"}]},{"featureType":"landscape","elementType":"all","stylers":[{"color":"#f9f5ed"}]},{"featureType":"road.highway","elementType":"all","stylers":[{"color":"#f5f5f5"}]},{"featureType":"road.highway","elementType":"geometry.stroke","stylers":[{"color":"#c9c9c9"}]},{"featureType":"water","elementType":"all","stylers":[{"color":"#aee0f4"}]}]}

      const mapView = new google.maps.Map(mapContainer, options)
      new google.maps.Marker({ position, map: mapView })

      this.mapsShow = true
      this.mapsLink = true
    },

    resetAddress() {
      this.googleAddress = {
        address: null,
      }

      this.mapsApiLoading = false
      this.mapsShow = false
      this.mapsLink = false
    },

    closeMap() {
      this.$nextTick(() => {
        this.mapsShow = false
      })
    },

    switchCountryCode() {
      if (!this.selectedCountry) return 'br'
      if (this.selectedCountry === 'BR') return 'br'
      if (this.selectedCountry === 'US') return 'au'
      if (this.selectedCountry === 'AU') return 'au'
    },
  },

  mounted() {
    this.initGooglePlaces()

    this.$nextTick(() => (this.googleAddress['address'] = this.address))

    this.$nextTick(() => (this.mapsField = this.$refs?.googleAddressAutocomplete?.$refs.input))

    if (this.getFocus) this.$nextTick(() => this.mapsField.focus())
  },

  beforeDestroy() {
    this.city = null
    this.route = null
    this.state = null
    this.country = null
    this.postalCode = null
    this.streetNumber = null

    this.googleAddress = {}

    this.showWarningForRoute = false
    this.showWarningForPostalCode = false
    this.showWarningForStreetNumber = false
    this.showWarningVicinity = false
  },
}
</script>
